/**
 * File: ExecutionTracePanel.tsx
 * Description: Displays the execution trace showing all tool calls and reasoning steps
 * Author: System
 * Created: 2026-02-09
 * Last Modified: 2026-02-16
 */

import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { ExecutionStep } from '../types/execution';

interface ExecutionTracePanelProps {
  trace: ExecutionStep[] | null;
  title?: string;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * ExecutionTracePanel component that displays agent execution steps
 * Shows tool calls, queries, and reasoning during generation/refinement
 */
export const ExecutionTracePanel: React.FC<ExecutionTracePanelProps> = ({
  trace,
  title = 'Execution Trace',
  isLoading = false,
  error = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const toggleStep = (stepNumber: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepNumber)) {
      newExpanded.delete(stepNumber);
    } else {
      newExpanded.add(stepNumber);
    }
    setExpandedSteps(newExpanded);
  };

  const getToolIcon = (toolName: string | undefined) => {
    if (!toolName) {
      return (
        <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
          <span className="text-xs text-gray-600">⚙️</span>
        </div>
      );
    }

    if (
      toolName.includes('brownfield') ||
      toolName.includes('search') ||
      toolName.includes('read')
    ) {
      return (
        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
          <span className="text-xs text-blue-600">🔍</span>
        </div>
      );
    } else if (toolName.includes('validate') || toolName.includes('refinement')) {
      return (
        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircleIcon className="w-4 h-4 text-green-600" />
        </div>
      );
    } else if (
      toolName.includes('generation') ||
      toolName.includes('refine') ||
      toolName.includes('generate')
    ) {
      return (
        <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
          <SparklesIcon className="w-4 h-4 text-purple-600" />
        </div>
      );
    }
    return (
      <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
        <span className="text-xs text-gray-600">⚙️</span>
      </div>
    );
  };

  if ( !trace) {
    return null;
  }

  // Filter out steps without a tool or with 'unknown' tool, and ensure step number exists
  const validSteps = trace.filter(
    (step): step is ExecutionStep & { step: number } => 
      Boolean(step.tool) && step.tool !== 'unknown' && typeof step.step === 'number',
  );

  if (validSteps.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
            {validSteps.length} steps
          </span>
        </div>
        {isExpanded ? (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 divide-y divide-gray-200">
          {isLoading && (
            <div className="px-4 py-6 flex items-center justify-center">
              <div className="animate-spin">
                <SparklesIcon className="w-5 h-5 text-blue-600" />
              </div>
              <span className="ml-2 text-sm text-gray-600">
                Executing steps...
              </span>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 bg-red-50 flex items-start gap-2">
              <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {!isLoading &&
            !error &&
            validSteps.map((step) => (
              <div
                key={step.step}
                className="hover:bg-gray-50 transition-colors"
              >
                <button
                  onClick={() => toggleStep(step.step)}
                  className="w-full px-4 py-3 flex items-start justify-between"
                >
                  <div className="flex items-start gap-3 flex-1">
                    {getToolIcon(step.tool)}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500">
                          Step {step.step}
                        </span>
                        <span className="text-sm font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          {step.tool}
                        </span>
                        {step.query && (
                          <span className="text-xs italic text-gray-600">
                            "{step.query}"
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mt-1">
                        {step.description ?? 'No description available'}
                      </p>
                      {step.duration_seconds && (
                        <p className="text-xs text-gray-500 mt-1">
                          {step.duration_seconds.toFixed(2)}s
                        </p>
                      )}
                    </div>
                  </div>
                  {step.output && (
                    <div className="ml-2">
                      {expandedSteps.has(step.step) ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  )}
                </button>

                {/* Step Output */}
                {expandedSteps.has(step.step) && step.output && (
                  <div className="px-4 pb-3 bg-gray-50">
                    <div className="bg-white rounded p-3 border border-gray-200 text-xs">
                      <p className="font-mono text-gray-600 whitespace-pre-wrap break-words">
                        {step.output}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ExecutionTracePanel;
